import { createSlice } from '@reduxjs/toolkit';
import {
  HomeObjective,
  HomeObjectiveSettings,
  HomeVideo,
  LaunchTasks,
  LaunchTaskType,
  StoreHomeObjectiveAdmin,
} from '../types';
import {
  createHomeVideoThunk,
  deleteHomeVideoThunk,
  fetchAllHomeVideosThunk,
  fetchCommonSettingsThunk,
  fetchHomeObjectiveThunk,
  fetchHomeVideosThunk,
  fetchLaunchTasksThunk,
  fetchStoreAdminViewThunk,
  reorderHomeVideosThunk,
  resetStoreOverrideThunk,
  setLaunchTaskCheckThunk,
  setManualValidationThunk,
  updateCommonSettingsThunk,
  updateHomeVideoThunk,
  updateStoreOverrideThunk,
} from './home.thunk';

export interface HomeState {
  objective: { data?: HomeObjective; storeId?: string; isLoading: boolean; hasError: boolean };
  launchTasks: {
    data?: LaunchTasks;
    storeId?: string;
    isLoading: boolean;
    hasError: boolean;
    savingType?: LaunchTaskType;
  };
  // A failed load just hides the section: the videos are secondary to the objective.
  videos: { items: HomeVideo[]; isLoading: boolean };
  admin: {
    common?: HomeObjectiveSettings;
    store?: StoreHomeObjectiveAdmin;
    requestedStoreId?: string;
    videos: HomeVideo[];
    isLoading: boolean;
    isSaving: boolean;
  };
}

export const initialState: HomeState = {
  objective: { isLoading: false, hasError: false },
  launchTasks: { isLoading: false, hasError: false },
  videos: { items: [], isLoading: false },
  admin: { videos: [], isLoading: false, isSaving: false },
};

const sortedVideos = (videos: HomeVideo[]) => [...videos].sort((a, b) => a.position - b.position);

export const { actions, reducer } = createSlice({
  name: 'home',
  initialState,
  reducers: {
    clearStoreAdminView: state => {
      state.admin.store = undefined;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchHomeObjectiveThunk.pending, (state, action) => {
        state.objective.storeId = action.meta.arg;
        state.objective.isLoading = true;
        state.objective.hasError = false;
      })
      // A response for a store that is no longer selected is dropped.
      .addCase(fetchHomeObjectiveThunk.fulfilled, (state, action) => {
        if (action.meta.arg !== state.objective.storeId) return;
        state.objective.data = action.payload;
        state.objective.isLoading = false;
      })
      .addCase(fetchHomeObjectiveThunk.rejected, (state, action) => {
        if (action.meta.arg !== state.objective.storeId) return;
        state.objective.data = undefined;
        state.objective.isLoading = false;
        state.objective.hasError = true;
      })
      .addCase(fetchLaunchTasksThunk.pending, (state, action) => {
        state.launchTasks.storeId = action.meta.arg;
        state.launchTasks.isLoading = true;
        state.launchTasks.hasError = false;
      })
      .addCase(fetchLaunchTasksThunk.fulfilled, (state, action) => {
        if (action.meta.arg !== state.launchTasks.storeId) return;
        state.launchTasks.data = action.payload;
        state.launchTasks.isLoading = false;
      })
      .addCase(fetchLaunchTasksThunk.rejected, (state, action) => {
        if (action.meta.arg !== state.launchTasks.storeId) return;
        state.launchTasks.data = undefined;
        state.launchTasks.isLoading = false;
        state.launchTasks.hasError = true;
      })
      .addCase(setLaunchTaskCheckThunk.pending, (state, action) => {
        state.launchTasks.savingType = action.meta.arg.type;
      })
      .addCase(setLaunchTaskCheckThunk.fulfilled, (state, action) => {
        state.launchTasks.savingType = undefined;
        if (action.payload.storeId === state.launchTasks.storeId) state.launchTasks.data = action.payload;
      })
      .addCase(setLaunchTaskCheckThunk.rejected, state => {
        state.launchTasks.savingType = undefined;
      })
      .addCase(fetchHomeVideosThunk.pending, state => {
        state.videos.isLoading = true;
      })
      .addCase(fetchHomeVideosThunk.fulfilled, (state, action) => {
        state.videos.items = action.payload;
        state.videos.isLoading = false;
      })
      .addCase(fetchHomeVideosThunk.rejected, state => {
        state.videos.isLoading = false;
      });

    [fetchCommonSettingsThunk, fetchAllHomeVideosThunk].forEach(thunk => {
      builder
        .addCase(thunk.pending, state => {
          state.admin.isLoading = true;
        })
        .addCase(thunk.rejected, state => {
          state.admin.isLoading = false;
        });
    });
    builder
      .addCase(fetchCommonSettingsThunk.fulfilled, (state, action) => {
        state.admin.common = action.payload;
        state.admin.isLoading = false;
      })
      .addCase(fetchStoreAdminViewThunk.pending, (state, action) => {
        state.admin.requestedStoreId = action.meta.arg;
        state.admin.isLoading = true;
      })
      // Switching stores quickly: only the last requested store may land in the editor.
      .addCase(fetchStoreAdminViewThunk.fulfilled, (state, action) => {
        if (action.meta.arg !== state.admin.requestedStoreId) return;
        state.admin.store = action.payload;
        state.admin.isLoading = false;
      })
      .addCase(fetchStoreAdminViewThunk.rejected, (state, action) => {
        if (action.meta.arg !== state.admin.requestedStoreId) return;
        state.admin.isLoading = false;
      })
      .addCase(fetchAllHomeVideosThunk.fulfilled, (state, action) => {
        state.admin.videos = action.payload;
        state.admin.isLoading = false;
      })
      .addCase(reorderHomeVideosThunk.fulfilled, (state, action) => {
        state.admin.videos = action.payload;
      });

    [
      updateCommonSettingsThunk,
      updateStoreOverrideThunk,
      resetStoreOverrideThunk,
      setManualValidationThunk,
      createHomeVideoThunk,
      updateHomeVideoThunk,
      deleteHomeVideoThunk,
    ].forEach(thunk => {
      builder
        .addCase(thunk.pending, state => {
          state.admin.isSaving = true;
        })
        .addCase(thunk.rejected, state => {
          state.admin.isSaving = false;
        });
    });
    builder
      .addCase(updateCommonSettingsThunk.fulfilled, (state, action) => {
        state.admin.common = action.payload;
        state.admin.isSaving = false;
      })
      .addCase(createHomeVideoThunk.fulfilled, (state, action) => {
        state.admin.videos = sortedVideos([...state.admin.videos, action.payload]);
        state.admin.isSaving = false;
      })
      .addCase(updateHomeVideoThunk.fulfilled, (state, action) => {
        state.admin.videos = sortedVideos(
          state.admin.videos.map(video => (video.id === action.payload.id ? action.payload : video)),
        );
        state.admin.isSaving = false;
      })
      .addCase(deleteHomeVideoThunk.fulfilled, (state, action) => {
        state.admin.videos = state.admin.videos.filter(video => video.id !== action.payload);
        state.admin.isSaving = false;
      });
    [updateStoreOverrideThunk, resetStoreOverrideThunk, setManualValidationThunk].forEach(thunk => {
      builder.addCase(thunk.fulfilled, (state, action) => {
        state.admin.store = action.payload;
        state.admin.isSaving = false;
      });
    });
  },
});
